import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { API_BASE, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { QRCodeCanvas } from "qrcode.react";
import { Plus, Search, Download, Pencil, Trash2, MapPin, X, Instagram, Facebook, Youtube, Linkedin, QrCode, ExternalLink } from "lucide-react";
import FileUploader from "@/components/FileUploader";
import HoursEditor from "@/components/HoursEditor";
import CsvImport from "@/components/CsvImport";
import { Clock } from "lucide-react";

const CATEGORIES = ["restauracio", "moda", "salut", "cultura", "serveis", "alimentacio", "esports", "altres"];

const empty = {
  commercial_name: "", legal_name: "", cif: "", dni_nif: "",
  address: "", phone: "", email: "", contact_person: "",
  category: "altres", status: "active",
  logo_url: "", gallery: [],
  website: "", virtual_tour_url: "",
  social: { instagram: "", facebook: "", tiktok: "", x: "", youtube: "", linkedin: "" },
  lat: null, lng: null,
  opening_hours: {}, holidays: [],
};

function resolveUrl(u) {
  if (!u) return "";
  if (u.startsWith("/")) return `${BACKEND_URL}${u}`;
  return u;
}

export default function AdminAssociates() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [geocoding, setGeocoding] = useState(false);
  const [qrFor, setQrFor] = useState(null);

  const downloadQr = () => {
    if (!qrFor) return;
    const canvas = document.querySelector(`#qr-canvas-${qrFor.member_number}`);
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a"); a.href = url; a.download = `qr-${qrFor.member_number}.png`; a.click();
  };

  const load = async () => {
    const { data } = await api.get("/associates", { params: { q } });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  const save = async () => {
    if (!form.commercial_name.trim()) return toast.error("Nom comercial obligatori");
    if (!form.email || !form.email.includes("@")) return toast.error("Email vàlid obligatori (identifica l'associat)");
    try {
      const payload = { ...form, social: form.social || {} };
      if (editing) await api.put(`/associates/${editing}`, payload);
      else await api.post("/associates", payload);
      toast.success(editing ? "Actualitzat" : "Associat creat i correu de benvinguda enviat");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Segur? Això elimina també el compte associat.")) return;
    await api.delete(`/associates/${id}`); toast.success("Eliminat"); load();
  };
  const edit = (a) => {
    setEditing(a.id);
    setForm({
      ...empty,
      ...a,
      gallery: a.gallery || [],
      social: { ...empty.social, ...(a.social || {}) },
      opening_hours: a.opening_hours || {},
      holidays: a.holidays || [],
    });
    setOpen(true);
  };
  const exportCsv = async () => {
    const token = localStorage.getItem("ss_token");
    const res = await fetch(`${API_BASE}/exports/associates.csv`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "associats.csv"; a.click();
  };

  const autoGeocode = async () => {
    if (!form.address?.trim()) return toast.error("Introdueix una adreça primer");
    setGeocoding(true);
    try {
      const q = `${form.address}, Salou, Tarragona, Spain`;
      const { data } = await api.get("/geocode", { params: { q } });
      if (data.lat && data.lng) {
        setForm({ ...form, lat: data.lat, lng: data.lng });
        toast.success("Coordenades trobades");
      } else {
        toast.error("Adreça no trobada — introdueix les coordenades manualment");
      }
    } catch { toast.error("Servei de geocoding no disponible"); }
    finally { setGeocoding(false); }
  };

  const removeGalleryItem = (idx) => {
    setForm({ ...form, gallery: form.gallery.filter((_, i) => i !== idx) });
  };

  return (
    <div className="space-y-6" data-testid="admin-associates">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline text-accent">Fitxa</div>
          <h1 className="mt-2 text-3xl font-heading font-bold tracking-tight">Associats</h1>
          <p className="text-sm text-muted-foreground mt-1">L'email identifica l'associat i genera el seu compte automàticament.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Cerca nom, CIF..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 rounded-full w-64" data-testid="associates-search-input" />
          </div>
          <Button variant="outline" className="rounded-full" onClick={exportCsv} data-testid="associates-export-btn"><Download className="w-4 h-4 mr-2" /> CSV</Button>
          <CsvImport onImported={load} />
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(empty); } }}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="associates-new-btn"><Plus className="w-4 h-4 mr-2" /> Nou associat</Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editing ? "Editar" : "Alta d'"} associat</DialogTitle></DialogHeader>

              <Tabs defaultValue="basic" className="w-full">
                <TabsList className="rounded-full w-full grid grid-cols-5">
                  <TabsTrigger value="basic" className="rounded-full" data-testid="tab-basic">Dades</TabsTrigger>
                  <TabsTrigger value="location" className="rounded-full" data-testid="tab-location">Ubicació</TabsTrigger>
                  <TabsTrigger value="brand" className="rounded-full" data-testid="tab-brand">Marca</TabsTrigger>
                  <TabsTrigger value="hours" className="rounded-full" data-testid="tab-hours"><Clock className="w-3.5 h-3.5 mr-1" /> Horaris</TabsTrigger>
                  <TabsTrigger value="links" className="rounded-full" data-testid="tab-links">Enllaços</TabsTrigger>
                </TabsList>

                <TabsContent value="basic" className="space-y-4 pt-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label>Email * <span className="text-xs text-muted-foreground">(identifica l'associat)</span></Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="assoc-form-email" /></div>
                    <div><Label>Nom comercial *</Label><Input required value={form.commercial_name} onChange={(e) => setForm({ ...form, commercial_name: e.target.value })} data-testid="assoc-form-commercial" /></div>
                    <div><Label>Raó social</Label><Input value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} data-testid="assoc-form-legal" /></div>
                    <div><Label>CIF empresa</Label><Input value={form.cif} onChange={(e) => setForm({ ...form, cif: e.target.value })} data-testid="assoc-form-cif" /></div>
                    <div><Label>DNI / NIF</Label><Input value={form.dni_nif} onChange={(e) => setForm({ ...form, dni_nif: e.target.value })} data-testid="assoc-form-dninif" /></div>
                    <div><Label>Persona de contacte</Label><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></div>
                    <div><Label>Telèfon</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="assoc-form-phone" /></div>
                    <div><Label>Categoria</Label>
                      <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Estat</Label>
                      <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="active">Actiu</SelectItem><SelectItem value="inactive">Inactiu</SelectItem></SelectContent>
                      </Select>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="location" className="space-y-4 pt-4">
                  <div><Label>Adreça</Label>
                    <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="assoc-form-address" />
                  </div>
                  <div>
                    <Button type="button" variant="outline" onClick={autoGeocode} disabled={geocoding} className="rounded-full" data-testid="assoc-geocode-btn">
                      <MapPin className="w-4 h-4 mr-2" /> {geocoding ? "Buscant..." : "Obtenir coordenades automàticament"}
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2">Utilitza OpenStreetMap per obtenir les coordenades a partir de l'adreça. Pots editar-les manualment.</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Latitud</Label><Input type="number" step="0.000001" value={form.lat ?? ""} onChange={(e) => setForm({ ...form, lat: e.target.value ? parseFloat(e.target.value) : null })} data-testid="assoc-form-lat" /></div>
                    <div><Label>Longitud</Label><Input type="number" step="0.000001" value={form.lng ?? ""} onChange={(e) => setForm({ ...form, lng: e.target.value ? parseFloat(e.target.value) : null })} data-testid="assoc-form-lng" /></div>
                  </div>
                  {form.lat && form.lng && (
                    <a href={`https://www.openstreetmap.org/?mlat=${form.lat}&mlon=${form.lng}#map=17/${form.lat}/${form.lng}`} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" /> Veure al mapa
                    </a>
                  )}
                </TabsContent>

                <TabsContent value="brand" className="space-y-6 pt-4">
                  <div>
                    <Label>Logo del establiment <span className="text-xs text-muted-foreground">(format 4:3)</span></Label>
                    <div className="mt-2 grid sm:grid-cols-[160px_1fr] gap-4 items-start">
                      <div className="aspect-[4/3] rounded-xl border-2 border-dashed border-border bg-secondary/40 overflow-hidden flex items-center justify-center" data-testid="assoc-logo-preview">
                        {form.logo_url ? (
                          <img src={resolveUrl(form.logo_url)} alt="logo" className="w-full h-full object-cover" />
                        ) : (
                          <div className="text-xs text-muted-foreground text-center px-2">Sense logo</div>
                        )}
                      </div>
                      <FileUploader
                        accept=".png,.jpg,.jpeg,.webp"
                        label="Puja el logo (4:3)"
                        onUploaded={(f) => setForm({ ...form, logo_url: f.url })}
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Galeria d'imatges <span className="text-xs text-muted-foreground">(format 16:9)</span></Label>
                    <div className="mt-2 space-y-3">
                      {form.gallery.length > 0 && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="assoc-gallery-grid">
                          {form.gallery.map((g, i) => (
                            <div key={i} className="relative aspect-video rounded-xl overflow-hidden border-2 border-border group">
                              <img src={resolveUrl(g)} alt="" className="w-full h-full object-cover" />
                              <button type="button" onClick={() => removeGalleryItem(i)} className="absolute top-1 right-1 w-7 h-7 rounded-full bg-white/90 text-destructive opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" data-testid={`assoc-gallery-remove-${i}`}>
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <FileUploader
                        accept=".png,.jpg,.jpeg,.webp"
                        label="Afegir imatge a la galeria (16:9)"
                        onUploaded={(f) => setForm({ ...form, gallery: [...form.gallery, f.url] })}
                      />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="hours" className="pt-4">
                  <HoursEditor
                    hours={form.opening_hours}
                    holidays={form.holidays}
                    onChange={({ hours, holidays }) => setForm({ ...form, opening_hours: hours, holidays })}
                  />
                </TabsContent>

                <TabsContent value="links" className="space-y-4 pt-4">
                  <div><Label>Web</Label><Input placeholder="https://..." value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} data-testid="assoc-form-website" /></div>
                  <div><Label>Enllaç tur virtual</Label><Input placeholder="https://..." value={form.virtual_tour_url} onChange={(e) => setForm({ ...form, virtual_tour_url: e.target.value })} data-testid="assoc-form-tour" /></div>

                  <div>
                    <Label>Xarxes socials</Label>
                    <div className="mt-2 space-y-2">
                      {[
                        { k: "instagram", icon: Instagram, ph: "https://instagram.com/..." },
                        { k: "facebook", icon: Facebook, ph: "https://facebook.com/..." },
                        { k: "tiktok", icon: null, ph: "https://tiktok.com/@..." },
                        { k: "x", icon: null, ph: "https://x.com/..." },
                        { k: "youtube", icon: Youtube, ph: "https://youtube.com/..." },
                        { k: "linkedin", icon: Linkedin, ph: "https://linkedin.com/company/..." },
                      ].map(({ k, icon: Icon, ph }) => (
                        <div key={k} className="flex items-center gap-2">
                          <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center shrink-0 capitalize text-xs font-semibold text-muted-foreground">
                            {Icon ? <Icon className="w-4 h-4" /> : k.slice(0, 2)}
                          </div>
                          <Input placeholder={ph} value={form.social?.[k] || ""} onChange={(e) => setForm({ ...form, social: { ...form.social, [k]: e.target.value } })} data-testid={`assoc-form-social-${k}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              <DialogFooter>
                <Button onClick={save} data-testid="assoc-form-save">{editing ? "Guardar canvis" : "Crear associat"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="rounded-2xl border-2 overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Núm.</TableHead><TableHead>Comerç</TableHead><TableHead>CIF / DNI</TableHead>
                <TableHead>Categoria</TableHead><TableHead>Contacte</TableHead><TableHead>Estat</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((a) => (
                <TableRow key={a.id} data-testid={`associate-row-${a.id}`}>
                  <TableCell className="font-mono text-xs">{a.member_number}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {a.logo_url ? (
                        <img src={resolveUrl(a.logo_url)} alt="" className="w-9 h-9 rounded-lg object-cover border" />
                      ) : (
                        <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center text-xs font-bold text-muted-foreground">
                          {a.commercial_name?.slice(0, 2).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div className="font-medium">{a.commercial_name}</div>
                        <div className="text-xs text-muted-foreground">{a.legal_name}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs font-mono">{a.cif || a.dni_nif || "—"}</TableCell>
                  <TableCell className="capitalize">{a.category}</TableCell>
                  <TableCell className="text-sm">{a.email}<br /><span className="text-xs text-muted-foreground">{a.phone}</span></TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-1 rounded-full ${a.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"}`}>{a.status}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => setQrFor(a)} title="Veure QR" data-testid={`associate-qr-${a.id}`}><QrCode className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => edit(a)} data-testid={`associate-edit-${a.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(a.id)} data-testid={`associate-delete-${a.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Sense resultats</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <Dialog open={!!qrFor} onOpenChange={(o) => !o && setQrFor(null)}>
        <DialogContent className="max-w-md" data-testid="qr-dialog">
          {qrFor && (
            <>
              <DialogHeader><DialogTitle>QR de {qrFor.commercial_name}</DialogTitle></DialogHeader>
              <div className="flex flex-col items-center gap-4 py-4">
                <div className="p-4 bg-white rounded-2xl border-2 border-border">
                  <QRCodeCanvas id={`qr-canvas-${qrFor.member_number}`} value={`${window.location.origin}/comerc/${qrFor.member_number}`} size={220} fgColor="#CF0283" bgColor="#ffffff" level="M" />
                </div>
                <div className="text-xs text-muted-foreground text-center">
                  Escaneja per obrir la fitxa pública<br />
                  <a href={`${window.location.origin}/comerc/${qrFor.member_number}`} target="_blank" rel="noreferrer" className="text-primary hover:underline break-all">{`${window.location.origin}/comerc/${qrFor.member_number}`}</a>
                </div>
                <div className="flex gap-2">
                  <Button onClick={downloadQr} className="rounded-full" data-testid="qr-download-btn"><Download className="w-4 h-4 mr-2" /> Descarregar PNG</Button>
                  <Button asChild variant="outline" className="rounded-full" data-testid="qr-open-btn">
                    <a href={`${window.location.origin}/comerc/${qrFor.member_number}`} target="_blank" rel="noreferrer"><ExternalLink className="w-4 h-4 mr-2" /> Obrir</a>
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground text-center px-6">Imprimeix aquest QR i enganxa'l a l'aparador. Els clients podran veure la fitxa del comerç al mòbil.</p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
